<template>
  <div>
    <tool-bar :title="toolbarTitle"></tool-bar>
    <loading :loading="loading"></loading>

    <error-alert-item
      :error-alert-event="errorAlertEvent"
      :network-error="networkError"
      @resetErrorAlertEvent="errorAlertEvent=false">
    </error-alert-item>

    <stage-comment-item></stage-comment-item>

    <refresh-item
      :show="showRefreshItem"
      @onRefresh="fetchInitialData"
    ></refresh-item>

    <div class="margin_top_20" v-if="showUI">
      <div class="item-heading-desc-div margin-left-10 margin-right-10">
        <weex-text class="item-heading">{{addressHeading}}</weex-text>
      </div>

    <wxc-checkbox
        v-show="showSameAsCurrentAddressCheckbox && propShowSameAsCurrentAddressCheckbox"
        class="margin-left-10 margin-right-10"
        v-for="item in sameAsCurrentAddressCheckboxList"
        :key="item"
        :config="checkBoxConfig"
        :checked="sameAsCurrentAddressDefaultSelection"
        :title="item"
        :value="item"
        @wxcCheckBoxItemChecked="onSameAsCurrentAddressCheckboxClicked"
    ></wxc-checkbox>

      <kyc-address-proof-document
        v-if="addressProofRequired"
        :kyc-osv-details-config="kycOsvDetailsConfig"
        @addressProofAttached="addressProofAttached=true"
        @kycAddressProofDocumentUploadCompeleted="saveAddress"
        ref="kycAddressProofDocumentItem"
      ></kyc-address-proof-document>

      <div class="dropdown_container" v-if="isFieldNeeded('address_ownership_type')">
        <wxc-dropdown
          default-item="Select"
          :title="getFieldLabel('address_ownership_type')"
          :select-item="selectOwnership"
          :config="dropdownConfig"
          :list="addressOwnershipList"
          @onItemSelected="onOwnershipSelected"
        ></wxc-dropdown>
      </div>

      <div class="input_container">
        <weex-text class="input_label">Flat/HouseNo./Floor/Building</weex-text>
        <input
          class="input_with_border_bottom"
          :value="getFieldValue('street')"
          @input="inputEntered('street', $event.target.value)"
          return-key-type="next">
      </div>

      <div class="input_container">
        <weex-text class="input_label">Colony/Street/Locality</weex-text>
        <input
          class="input_with_border_bottom"
          :value="getFieldValue('addressLine1')"
          @input="inputEntered('addressLine1', $event.target.value)"
          return-key-type="next">
      </div>

      <div class="input_container">
        <weex-text class="input_label">Landmark</weex-text>
        <weex-text class="input_label_des">(Optional)</weex-text>
        <input
          class="input_with_border_bottom"
          :value="getFieldValue('addressLine2')"
          @input="inputEntered('addressLine2', $event.target.value)"
          return-key-type="next">
      </div>

      <div class="input_container">
        <weex-text class="input_label">Pin Code</weex-text>
        <input
          class="input_with_border_bottom"
          :value="getFieldValue('postalCode')"
          @input="postalEntered($event.target.value)"
          type="number"
          maxlength="6"
          return-key-type="next">
      </div>

      <div class="dropdown_container">
        <wxc-dropdown
          title="State"
          default-item="Select State"
          :config="dropdownConfig"
          :disabled="stateDropdownDisabled"
          :select-item="selectState"
          :list="stateList"
          @onItemSelected="onStateSelected"
        ></wxc-dropdown>
      </div>

      <div class="input_container">
        <weex-text class="input_label">District</weex-text>
        <input
          :readonly="disableAddressLine3"
          class="input_with_border_bottom"
          :value="getFieldValue('addressLine3')"
          @input="inputEntered('addressLine3', $event.target.value)"
          return-key-type="next">
      </div>

      <div class="input_container">
        <weex-text class="input_label">City</weex-text>
        <input
          :readonly="disableCity"
          class="input_with_border_bottom"
          :value="getFieldValue('city')"
          @input="inputEntered('city', $event.target.value)"
          return-key-type="done">
      </div>

      <div class="input_container" v-if="isFieldNeeded('monthly_rent')">
        <weex-text class="input_label">{{getFieldLabel('monthly_rent')}}</weex-text>
        <div class="item-price-input-container">
          <input
            :readonly="true"
            class="item-price-label-input"
            :value="rupeeSign" />
          <input
            class="item-price-input"
            :value="getDynamicFieldValue('monthly_rent')"
            @input="onDynamicFieldEntered('monthly_rent', $event.target.value)"
            type="number" />
        </div>
        <field-error-item
          :show-error="showFieldError('monthly_rent')"
          :error-msg="getErrorMsg('monthly_rent')"
        ></field-error-item>
      </div>

      <div class="item-row-alignment margin_top_20">
        <div
          style="width: 50%; margin: 0 5px 0 10px"
          v-if="isFieldNeeded('years_at_address')">
          <weex-text class="input_label">{{getFieldLabel('years_at_address')}}</weex-text>
          <input
            class="input_with_border_bottom"
            :value="getDynamicFieldValue('years_at_address')"
            @input="onDynamicFieldEntered('years_at_address', $event.target.value)"
            return-key-type="next">
          <field-error-item
            :show-error="showFieldError('years_at_address')"
            :error-msg="getErrorMsg('years_at_address')"
          ></field-error-item>
        </div>

        <div style="width: 50%; margin: 0 10px 0 5px"
             v-if="isFieldNeeded('months_at_address')">
          <weex-text class="input_label">{{getFieldLabel('months_at_address')}}</weex-text>
          <input
            class="input_with_border_bottom"
            :value="getDynamicFieldValue('months_at_address')"
            @input="onDynamicFieldEntered('months_at_address', $event.target.value)"
            return-key-type="next">
          <field-error-item
            :show-error="showFieldError('months_at_address')"
            :error-msg="getErrorMsg('months_at_address')"
          ></field-error-item>
        </div>
      </div>

      <div class="item-row-alignment margin_top_20">
        <div
          style="width: 50%; margin: 0 5px 0 10px"
          v-if="isFieldNeeded('years_at_city')">
          <weex-text class="input_label">{{getFieldLabel('years_at_city')}}</weex-text>
          <input
            class="input_with_border_bottom"
            :value="getDynamicFieldValue('years_at_city')"
            @input="onDynamicFieldEntered('years_at_city', $event.target.value)"
            return-key-type="next">
          <field-error-item
            :show-error="showFieldError('years_at_city')"
            :error-msg="getErrorMsg('years_at_city')"
          ></field-error-item>
        </div>

        <div style="width: 50%; margin: 0 10px 0 5px"
             v-if="isFieldNeeded('months_at_city')">
          <weex-text class="input_label">{{getFieldLabel('months_at_city')}}</weex-text>
          <input
            class="input_with_border_bottom"
            :value="getDynamicFieldValue('months_at_city')"
            @input="onDynamicFieldEntered('months_at_city', $event.target.value)"
            return-key-type="next">
          <field-error-item
            :show-error="showFieldError('months_at_city')"
            :error-msg="getErrorMsg('months_at_city')"
          ></field-error-item>
        </div>
      </div>

      <wxc-button
        style="margin-top: 40px; margin-bottom: 20px"
        text="Next"
        class="button_horizontal_center"
        type="blue"
        @wxcButtonClicked="onSubmit"
      ></wxc-button>
    </div>
  </div>
</template>

<script>
import ToolBar from "../layout/ToolBar";
import Loading from "../layout/Loading";
import ActivityRedirectItem from "../layout/ActivityRedirectItem";
import StageCommentItem from "../layout/StageCommentItem";
import ErrorAlertItem from "../layout/ErrorAlertItem";
import WxcButton from "../../../weex-ui/packages/wxc-button";
import WxcDropdown from "../../../weex-ui/packages/wxc-dropdown";
import EzUtils from "../../utils/EzUtils";
import LoanTasksActivityNavigator from "../../utils/LoanTasksActivityNavigator";
import DataTableUtils from "../../utils/DataTableUtils";
import { DefaultResourceConfig } from "../../utils/ImageResourcesUtil";
import { MessageConstants } from "../../utils/MessageConstantUtil";
import { Activities } from "../../utils/ActivityNavigator";
import LoanTasksUtility from "../../utils/LoanTasksUtility";
import LoanScopeDataHolder from "../../data/model/LoanScopeDataHolder";
import DataSource from "../../data/DataSource";
import AddressResponse from "../../data/model/AddressResponse";
import AlertService from "../core/alert/AlertService";
import PrefManager from "../../data/model/PrefManager";
import { RolesConstants } from "../../utils/RolesConstants";
import RefreshItem from "../layout/RefreshItem";
import FieldErrorItem from "../layout/FieldErrorItem";
import DynamicFieldUtils, { FieldTypes } from "../../utils/DynamicFieldUtils";
import { DataTables } from "../../utils/DataTables";
import storageUtil from "../../utils/StorageUtil";
import KycAddressProofDocument from "../layout/KycAddressProofDocument";
import WxcCheckbox from "../../../weex-ui/packages/wxc-checkbox";

const DEFAULT_ADDRESS_LIST = ["AADHAAR", "VOTER_ID", "ELECTRICITY_BILL"];
const REGEX_YEARS = "^[0-9]{1,2}$";
const REGEX_MONTH = "^(0|0?[1-9]|1[01])$";
const REGEX_AMOUNT = "(^[0-9]+[.]*[0-9]+$|^[0-9]+$)";
const RUPEE = "\u20B9";
const ALL_FIELDS_DATA = {
  monthly_rent: {
    key: "monthly_rent",
    label: "Monthly Rent",
    validation: REGEX_AMOUNT,
    fieldType: "EDIT_FIELD",
    errorMsg: "Enter valid amount.",
    optional: false
  },

  years_at_address: {
    key: "years_at_address",
    label: "Years at Address",
    validation: REGEX_YEARS,
    fieldType: "EDIT_FIELD",
    errorMsg: "Enter valid no of Years.",
    optional: false
  },

  months_at_address: {
    key: "months_at_address",
    label: "Months at Address",
    validation: REGEX_MONTH,
    fieldType: "EDIT_FIELD",
    errorMsg: "Allowed value: 0-11",
    optional: false
  },

  years_at_city: {
    key: "years_at_city",
    label: "Years at City",
    validation: REGEX_YEARS,
    fieldType: "EDIT_FIELD",
    errorMsg: "Enter valid no of Years.",
    optional: false
  },

  months_at_city: {
    key: "months_at_city",
    label: "Months at City",
    validation: REGEX_MONTH,
    fieldType: "EDIT_FIELD",
    errorMsg: "Allowed value: 0-11",
    optional: false
  },

  address_ownership_type: {
    key: "address_ownership_type",
    label: "Address Ownership Type",
    validation: "",
    fieldType: FieldTypes.DROPDOWN,
    errorMsg: "Please select Address Ownership Type.",
    optional: true
  },
};

export default {
  name: "AddressActivity",
  components: {
    ActivityRedirectItem,
    ErrorAlertItem,
    Loading,
    ToolBar,
    WxcButton,
    WxcDropdown,
    StageCommentItem,
    RefreshItem,
    FieldErrorItem,
    KycAddressProofDocument,
    WxcCheckbox,
  },
  
  props : {
    addressType: {
      type: String,
      required: true
    },
    propShowSameAsCurrentAddressCheckbox : {
      type: Boolean,
      required: false,
      default : false
    },
    tasks: {
      type: Array,
      required: true,
    },
    currentTaskName: {
      type: String,
      required: true
    }
  },

  data: () => ({
    address: {
      street: "",
      addressLine1: "",
      addressLine2: "",
      addressLine3: "",
      city: "",
      postalCode: "",
      stateProvinceId: -1,
      addressTypeId: -1,
      countryId: -1,
      addressOwnershipTypeId: null,
      monthsAtAddress: null,
      yearsAtAddress: null,
      monthlyRent : null,
      yearsAtCity : null,
      monthsAtCity : null,
    },
    addressTimeData: {
      years_at_address: null,
      months_at_address: null,
      years_at_city: null,
      months_at_city: null,
      monthly_rent: null
    },
    showUI: false,
    stateList: [],
    selectState: null,
    disableCity: false,
    disableAddressLine3: false,
    stateDropdownDisabled: false,
    stateProvinceIdOptions: [],
    allowedAddressProofsList: [],
    loading: false,
    errorAlertEvent: false,
    networkError: {},
    showRefreshItem: false,
    addressTypeId: -1,
    countryId: -1,
    dynamicFields: null,
    invalidFields: [],
    addressProofRequired: true,
    kycStageDataConfig: {},
    kycAddressProofDocumentUploadCompeleted: false,
    addressProofAttached: false,
    addressOwnershipList: [],
    addressOwnerTypeIdOptions: [],
    selectOwnership : null,
    sameAsCurrentAddressCheckboxList: ["Same as Current Address"],
    showSameAsCurrentAddressCheckbox: false,
    sameAsCurrentAddressDefaultSelection: false,
    currentAddress : {},
    homeAddressTypeId : -1,
  }),
  methods: {
    isFieldNeeded(key) {
      return this.dynamicFields == null || this.dynamicFields[key] != null;
    },

    getFieldLabel(key) {
      return this.dynamicFields && this.dynamicFields[key] && this.dynamicFields[key]['label']
        ? this.dynamicFields[key]["label"]
        : ALL_FIELDS_DATA[key]["label"];
    },

    getFieldValue(key) {
      return this.address && this.address[key] ? this.address[key] : "";
    },

    onDynamicFieldEntered(key, value) {
      this.addressTimeData[key] = value;
      this.addressTimeDataAndAddressObjectMapping(key, value);
      this.removeFromInvalidFieldsIfValid(key);
    },

    addressTimeDataAndAddressObjectMapping(key, value){
      let addressTimeAndAddressMap = {
        years_at_address : "yearsAtAddress",
        months_at_address : "monthsAtAddress",
        monthly_rent : "monthlyRent",
        years_at_city : "yearsAtCity",
        months_at_city : "monthsAtCity"

      }
      if(addressTimeAndAddressMap[key]){
        this.inputEntered(addressTimeAndAddressMap[key],value);
      }
    },

    removeFromInvalidFieldsIfValid(key) {
      this.invalidFields = DynamicFieldUtils.removeFromInvalidFieldsIfValidValue(
        this.invalidFields,
        key,
        this.addressTimeData[key],
        ALL_FIELDS_DATA,
        this.dynamicFields
      );
    },

    getDynamicFieldValue(key) {
      return this.addressTimeData && this.addressTimeData[key] ? this.addressTimeData[key] : "";
    },

    showFieldError(key) {
      return this.invalidFields && this.invalidFields.includes(key);
    },

    getErrorMsg(key) {
      return ALL_FIELDS_DATA[key]["errorMsg"];
    },

    inputEntered(key, value) {
      this.address[key] = value;
    },

    onStateSelected(e) {
      this.onStateEntered(e.value);
    },

    onOwnershipSelected(e) {
      this.onOwnershipEntered(e.value);
    },

    onStateEntered(state) {
      this.selectState = state;
      this.inputEntered(AddressResponse.STATE_PROVINCE_ID,
        parseInt(DataTableUtils.getOptionId(this.stateProvinceIdOptions, state))
      );
    },

    onOwnershipEntered(ownership) {
      this.selectOwnership = ownership
      this.inputEntered(AddressResponse.ADDRESS_OWNERSHIP_TYPE,
        parseInt(DataTableUtils.getOptionId(this.addressOwnerTypeIdOptions, ownership))
      );
    },


    postalEntered(postalCode) {
      this.inputEntered(AddressResponse.POSTAL_CODE, postalCode);
      this.getPostalCodeDetails();
    },

    getPostalCodeDetails() {
      if (EzUtils.validatePostalCode(this.getFieldValue(AddressResponse.POSTAL_CODE))) {
        this.loading = true;
        DataSource.getPincodeDetails(this.getFieldValue(AddressResponse.POSTAL_CODE))
          .then(postalCodeRes => {
            this.loading = false;
            if (postalCodeRes.errorOccurred) {
              this.selectState = "";
              this.stateDropdownDisabled = false;
              this.disableCity = false;
              this.disableAddressLine3 = false;
            } else {
              const postalCodeData = postalCodeRes.data ? postalCodeRes.data : {};
              if (this.isValidState(postalCodeData.state)) {
                this.selectState = postalCodeData.state;
                this.stateDropdownDisabled = true;
              } else {
                this.stateDropdownDisabled = false;
                this.selectState = "";
              }
              if (postalCodeData.district) {
                this.inputEntered(AddressResponse.ADDRESS_LINE3, postalCodeData.district);
                this.inputEntered(AddressResponse.CITY, postalCodeData.district);
                this.disableCity = true;
                this.disableAddressLine3 = true;
              } else {
                this.disableCity = false;
                this.disableAddressLine3 = false;
              }
            }
          });
      } else {
        this.selectState = "";
        this.stateDropdownDisabled = false;
        this.disableCity = false;
        this.disableAddressLine3 = false;
      }
    },

    isValidState(state) {
      let validState = false;
      this.stateList && this.stateList.forEach(item => {
        if (item && item === state) {
          validState = true;
        }
      });

      return validState;
    },

    onSubmit() {
      if (this.checkForValidation()) {
        if(this.addressProofRequired){
          this.$refs.kycAddressProofDocumentItem.onContinueClicked();
        }else{
          this.saveAddress();
        }
      }
    },

    checkForValidation() {
      this.invalidFields = DynamicFieldUtils.validateDynamicFieldsForRequiredFields(
        this.addressTimeData,
        ALL_FIELDS_DATA,
        this.dynamicFields
      );
      if (!this.getFieldValue(AddressResponse.STREET)
        || !this.getFieldValue(AddressResponse.ADDRESS_LINE1)
        || !this.getFieldValue(AddressResponse.ADDRESS_LINE3)
        || !this.getFieldValue(AddressResponse.CITY)
        || !this.getFieldValue(AddressResponse.POSTAL_CODE)
      ) {
        this.showToast(MessageConstants.EMPTY_FIELD_MESSAGE);
        return false;
      }

      if (!this.isValidStreetAddress()) {
        this.showToast(MessageConstants.INVALID_STREET_ADDRESS_MESSAGE);
        return false;
      }

      if (!EzUtils.validatePostalCode(this.getFieldValue(AddressResponse.POSTAL_CODE))) {
        this.showToast(MessageConstants.INVALID_POSTAL_CODE_MESSAGE);
        return false;
      }

      if (!this.getFieldValue(AddressResponse.STATE_PROVINCE_ID)
        || parseInt(this.getFieldValue(AddressResponse.STATE_PROVINCE_ID)) === -1
      ) {
        this.showToast(MessageConstants.SELECT_STATE_MESSAGE);
        return false;
      }

      if (this.isFieldNeeded(ALL_FIELDS_DATA.address_ownership_type.key) &&
        (!this.getFieldValue(AddressResponse.ADDRESS_OWNERSHIP_TYPE) 
        || parseInt(this.getFieldValue(AddressResponse.ADDRESS_OWNERSHIP_TYPE)) === -1)
      ) {
        this.showToast(MessageConstants.SELECT_ADDRESS_OWNERSHIP_TYPE_MESSAGE);
        return false;
      }

      if(this.addressProofRequired && !this.addressProofAttached){
        this.showToast(MessageConstants.UPLOAD_ADDRESS_PROOF_MESSAGE);
        return false;
      }

      return !this.invalidFields || this.invalidFields.length === 0;
    },

    isValidStreetAddress() {
      let streetArray = this.getFieldValue(AddressResponse.STREET).trim().split(" ");
      if (streetArray.length < 2) {
        return false;
      }
      let streetItemCount = 0;
      streetArray.forEach(streetSplitValue => {
        streetItemCount = (streetSplitValue.trim().length > 1) ? streetItemCount + 1
          : streetItemCount;
      });
      return (streetItemCount > 0);
    },

    showToast(message) {
      AlertService.showToast(message);
    },

    showError(error) {
      this.loading = false;
      this.networkError = error;
      this.errorAlertEvent = true;
    },

    saveAddress() {
      //TODO check pincode validation on server same as agent app
      const addressPayload = AddressResponse.getAddressPayload(this.address);
      this.loading = true;
      DataSource.saveClientAddress(LoanScopeDataHolder.getClientId(),
        this.address[AddressResponse.ADDRESS_TYPE_ID], addressPayload)
        .then(clientAddressSaveRes => {
          if (clientAddressSaveRes.errorOccurred) {
            this.showError(clientAddressSaveRes.data);
          } 
          else {
            this.completeCurrentTaskAndGetNextTask();
          }
        })
    },

    // saveAddressAndCityInfoIfNeeded() {
    //   const addressAndCityInfo = {};
    //   let needToStoreData = false;
    //   for (const [key, value] of Object.entries(this.addressTimeData)) {
    //     if (value) {
    //       addressAndCityInfo[key] = value;
    //       needToStoreData = true;
    //     }
    //   }
    //   if (needToStoreData) {
    //     this.loading = true;
    //     DataSource.getDataTableInfo(DataTables.PERSONAL_DETAILS, LoanScopeDataHolder.getClientId())
    //       .then(personalDetailsRes => {
    //         if (personalDetailsRes.errorOccurred) {
    //           this.showError(personalDetailsRes.data)
    //         } else {
    //           if (EzUtils.isNotEmpty(personalDetailsRes.data)) {
    //             DataSource.updateDataTableEntry(DataTables.PERSONAL_DETAILS,
    //               LoanScopeDataHolder.getClientId(),
    //               addressAndCityInfo
    //             )
    //               .then(res => {
    //                 this.loading = false;
    //                 if (res.errorOccurred) {
    //                   this.showError(res.data)
    //                 } else {
    //                   this.completeCurrentTaskAndGetNextTask();
    //                 }
    //               })
    //           } else {
    //             DataSource.addDataTableEntry(
    //               DataTables.PERSONAL_DETAILS,
    //               LoanScopeDataHolder.getClientId(),
    //               addressAndCityInfo
    //             )
    //               .then(res => {
    //                 this.loading = false;
    //                 if (res.errorOccurred) {
    //                   this.showError(res.data)
    //                 } else {
    //                   this.completeCurrentTaskAndGetNextTask();
    //                 }
    //               })
    //           }
    //         }
    //       })
    //   } else {
    //     this.completeCurrentTaskAndGetNextTask();
    //   }
    // },

    completeCurrentTaskAndGetNextTask() {
      this.loading = true;
      LoanTasksActivityNavigator.getNextTask(this.tasks)
        .then(res => {
          this.loading = false;
          if (res.errorOccurred) {
            this.showError(res.data);
          }
        });
    },

    filterState() {
      this.stateList = [];
      let states = [];
      this.stateProvinceIdOptions.forEach(code => {
        states.push(code.name);
      });
      states.sort();
      this.stateList = states;
    },

    fetchInitialData() {
      this.showRefreshItem = false;
      this.loading = true;
      this.addressProofRequired = false;
      LoanTasksUtility.getLoanStageConfigData(LoanScopeDataHolder.getLoanId(), this.currentTaskName)
      .then(stageConfigDataRes => {
          if (!stageConfigDataRes.errorOccurred) {
            const addressStageConfigData = stageConfigDataRes.data;
            if(addressStageConfigData.properties){
                this.addressProofRequired = addressStageConfigData.properties["address.proof.required"];
                const allowedAddressProofs = addressStageConfigData.properties["address.proof.required.list"];
                this.kycStageDataConfig = {kycOSVDetailsDto : { allowedAddressProofs :[] }};
                this.kycStageDataConfig.kycOSVDetailsDto.allowedAddressProofs = allowedAddressProofs;
                this.allowedAddressProofsList = allowedAddressProofs;
            }
            this.getAddressTemplates();
          }else{
              this.showError(stageConfigDataRes.data);
              this.showRefreshItem = true;
          }
      });

      // LoanTasksUtility.getLoanStageConfigData(LoanScopeDataHolder.getLoanId(), "KYC")
      //     .then(stageConfigDataRes => {
      //     if (stageConfigDataRes.errorOccurred) {
      //         this.showError(stageConfigDataRes.data);
      //         this.showRefreshItem = true;
      //     } else {
      //         const stageConfigData = stageConfigDataRes.data;
      //         this.kycStageDataConfig = stageConfigData;
      //         if (stageConfigData
      //         && stageConfigData.kycOSVDetailsDto
      //         && stageConfigData.kycOSVDetailsDto.allowedAddressProofs
      //         ) {
      //         this.allowedAddressProofsList = stageConfigData.kycOSVDetailsDto.allowedAddressProofs
      //         }
      //         this.getAddressTemplates();
      //     }
      //     });
    },

    getAddressTemplates(){
        DataSource.getAddressTemplates()
            .then(addressTemplateRes => {
            this.loading = false;
            if (addressTemplateRes.errorOccurred) {
                this.showError(addressTemplateRes.data);
                this.showRefreshItem = true;
            } else {
                this.addressTypeId = DataTableUtils.getOptionId(
                addressTemplateRes.data[AddressResponse.ADDRESS_TYPE_ID_OPTIONS],
                this.addressType
                );
                this.countryId = DataTableUtils.getOptionId(
                addressTemplateRes.data[AddressResponse.COUNTRY_ID_OPTIONS],
                "India"
                );
            this.homeAddressTypeId = DataTableUtils.getOptionId(
                addressTemplateRes.data[AddressResponse.ADDRESS_TYPE_ID_OPTIONS],
                AddressResponse.HOME_ADDRESS_TYPE
            );
                this.stateProvinceIdOptions = addressTemplateRes.data[AddressResponse.STATE_PROVINCE_ID_OPTIONS];
                this.addressOwnerTypeIdOptions = addressTemplateRes.data[AddressResponse.ADDRESS_OWNERSHIP_TYPE_OPTIONS];
                this.filterState();
                this.populateAddressOwnershipList();
                if(this.fetchLeadAddress){
                  this.fetchLeadOrClientAddress();
                }else{
                  this.fetchClientAddress(true);
                }
            }
            })
    },

    fetchLeadOrClientAddress() {
      this.loading = true;
      if (EzUtils.isValidId(LoanScopeDataHolder.getLeadId())) {
        DataSource.getLeadDetails(LoanScopeDataHolder.getLeadId())
          .then(leadDetailsRes => {
            this.loading = false;
            if (leadDetailsRes.errorOccurred) {
              this.showError(leadDetailsRes.data);
              this.showRefreshItem = true;
            } else {
              const leadDetails = leadDetailsRes.data;
              const leadDetailsData = leadDetails && leadDetails.data
                ? EzUtils.convertToJSONObjectIfString(leadDetails.data)
                : {};
              if (leadDetailsData
                && leadDetailsData.addresses
                && leadDetailsData.addresses.length > 0
              ) {
                this.showUI = true;
                const addresses = leadDetailsData.addresses;
                let address = addresses[addresses.length - 1];
                let leadType = this.getAddressLeadType;
                addresses.forEach(addressInLead => {
                  if (addressInLead
                    && EzUtils.areEqualString(leadType, addressInLead.type, true)
                  ) {
                    address = addressInLead;
                  }
                });
                this.inputEntered(AddressResponse.STATE_PROVINCE_ID, -1);
                this.inputEntered(AddressResponse.ADDRESS_TYPE_ID, this.addressTypeId);
                this.inputEntered(AddressResponse.COUNTRY_ID, this.countryId);
                this.inputEntered(AddressResponse.STREET, address.street);
                this.inputEntered(AddressResponse.ADDRESS_LINE1, address.address_line_1);
                this.inputEntered(AddressResponse.ADDRESS_LINE2, address.address_line_2);
                this.inputEntered(AddressResponse.ADDRESS_LINE3, address.address_line_3);
                this.inputEntered(AddressResponse.CITY, address.city);
                this.postalEntered(address.pincode);
                this.fetchClientAddress(false);
              } else {
                this.fetchClientAddress(true);
              }
            }
          })
      } else {
        this.fetchClientAddress(true);
      }
    },

    fetchClientAddress(populateFields) {
      this.loading = true;
      let adderssTypeId = this.propShowSameAsCurrentAddressCheckbox ? this.homeAddressTypeId : this.addressTypeId;
      DataSource.getClientAddress(
        LoanScopeDataHolder.getClientId(),
        new Array(true),
        adderssTypeId
      )
        .then(clientAddressRes => {
          this.loading = false;
          if (clientAddressRes.errorOccurred) {
            this.showError(clientAddressRes.data);
            this.showRefreshItem = true;
          } else {
            this.showUI = true;
            const addresses = clientAddressRes.data;
            if(populateFields){
              this.inputEntered(AddressResponse.STATE_PROVINCE_ID, -1);
              this.inputEntered(AddressResponse.ADDRESS_TYPE_ID, this.addressTypeId);
              this.inputEntered(AddressResponse.COUNTRY_ID, this.countryId);
              if (EzUtils.isNotEmpty(addresses)) {
                const address = addresses[addresses.length - 1];
                this.inputEntered(AddressResponse.STREET, address[AddressResponse.STREET]);
                this.inputEntered(
                  AddressResponse.ADDRESS_LINE1,
                  address[AddressResponse.ADDRESS_LINE1]
                );
                this.inputEntered(
                  AddressResponse.ADDRESS_LINE2,
                  address[AddressResponse.ADDRESS_LINE2]
                );
                this.inputEntered(
                  AddressResponse.ADDRESS_LINE3,
                  address[AddressResponse.ADDRESS_LINE3]
                );
                this.inputEntered(AddressResponse.CITY, address[AddressResponse.CITY]);
                this.postalEntered(address[AddressResponse.POSTAL_CODE]);
                this.selectAddressOwnership(address[AddressResponse.ADDRESS_OWNERSHIP_TYPE]);
                this.onDynamicFieldEntered(ALL_FIELDS_DATA.years_at_city.key, address[AddressResponse.YEARS_AT_CITY]);
                this.onDynamicFieldEntered(ALL_FIELDS_DATA.months_at_city.key, address[AddressResponse.MONTHS_AT_CITY]);
                this.onDynamicFieldEntered(ALL_FIELDS_DATA.monthly_rent.key, address[AddressResponse.MONTHLY_RENT]);
                this.onDynamicFieldEntered(ALL_FIELDS_DATA.years_at_address.key, address[AddressResponse.YEAR_AT_ADDRESS]);
                this.onDynamicFieldEntered(ALL_FIELDS_DATA.months_at_address.key, address[AddressResponse.MONTH_AT_ADDRESS]);
              }
            }
            if(this.propShowSameAsCurrentAddressCheckbox && addresses[addresses.length - 1]){
              this.currentAddress = addresses[addresses.length - 1];
              this.showSameAsCurrentAddressCheckbox = true;
              if(populateFields){
                this.sameAsCurrentAddressDefaultSelection = true;
              }
            }
          }
        })
    },

    fillAddressWithCurrentAddress() {
      this.inputEntered(AddressResponse.STREET, this.currentAddress[AddressResponse.STREET]);
      this.inputEntered(
        AddressResponse.ADDRESS_LINE1,
        this.currentAddress[AddressResponse.ADDRESS_LINE1]
      );
      this.inputEntered(
        AddressResponse.ADDRESS_LINE2,
        this.currentAddress[AddressResponse.ADDRESS_LINE2]
      );
      this.inputEntered(
        AddressResponse.ADDRESS_LINE3,
        this.currentAddress[AddressResponse.ADDRESS_LINE3]
      );
      this.inputEntered(AddressResponse.CITY, this.currentAddress[AddressResponse.CITY]);
      this.postalEntered(this.currentAddress[AddressResponse.POSTAL_CODE]);
      this.onDynamicFieldEntered(ALL_FIELDS_DATA.years_at_city.key, this.currentAddress[AddressResponse.YEARS_AT_CITY]);
      this.onDynamicFieldEntered(ALL_FIELDS_DATA.months_at_city.key, this.currentAddress[AddressResponse.MONTHS_AT_CITY]);
      this.onDynamicFieldEntered(ALL_FIELDS_DATA.monthly_rent.key, this.currentAddress[AddressResponse.MONTHLY_RENT]);
      this.onDynamicFieldEntered(ALL_FIELDS_DATA.years_at_address.key, this.currentAddress[AddressResponse.YEAR_AT_ADDRESS]);
      this.onDynamicFieldEntered(ALL_FIELDS_DATA.months_at_address.key, this.currentAddress[AddressResponse.MONTH_AT_ADDRESS]);
      this.selectAddressOwnership(this.currentAddress[AddressResponse.ADDRESS_OWNERSHIP_TYPE]);
    },

    onSameAsCurrentAddressCheckboxClicked(e) {
      this.resetAddressField();
      if (e.checked) {
        this.fillAddressWithCurrentAddress();
      }
    },

    populateAddressOwnershipList() {
      this.addressOwnershipList = [];
      let ownerships = [];
      if(this.addressOwnerTypeIdOptions){
        this.addressOwnerTypeIdOptions.forEach(ownership => {
          ownerships.push(ownership.name);
        });
        ownerships.sort();
        this.addressOwnershipList = ownerships;
      }
    },

    selectAddressOwnership(id){
      let name = DataTableUtils.getOptionName(this.addressOwnerTypeIdOptions, id);
      if(name){
        this.onOwnershipEntered(name);
      }
    },

    resetAddressField() {
      this.inputEntered(AddressResponse.STREET, "");
      this.inputEntered(AddressResponse.ADDRESS_LINE1, "");
      this.inputEntered(AddressResponse.ADDRESS_LINE2, "");
      this.inputEntered(AddressResponse.ADDRESS_LINE3, "");
      this.inputEntered(AddressResponse.CITY, "");
      this.inputEntered(AddressResponse.STATE_PROVINCE_ID, -1);
      this.postalEntered("");
      this.onDynamicFieldEntered(ALL_FIELDS_DATA.years_at_city.key, "");
      this.onDynamicFieldEntered(ALL_FIELDS_DATA.months_at_city.key, "");
      this.onDynamicFieldEntered(ALL_FIELDS_DATA.monthly_rent.key, "");
      this.onDynamicFieldEntered(ALL_FIELDS_DATA.years_at_address.key, "");
      this.onDynamicFieldEntered(ALL_FIELDS_DATA.months_at_address.key, "");
      this.onOwnershipEntered("");
    },
  },

  created: function () {
    this.dynamicFields = DynamicFieldUtils.getRequiredDynamicFieldsData();
    this.fetchInitialData();
  },

  computed: {

    dropdownConfig() {
      return DefaultResourceConfig.DROP_DOWN;
    },

    addressHeading() {
      const addressOwner = this.isAgent ? "customer's" : "your";
      switch (this.addressType) {

        case AddressResponse.HOME_ADDRESS_TYPE:
          if(!this.addressProofRequired){
            return "Please enter customer’s residence address."
          }else{
            return `Please enter ${addressOwner} residence address. Address has to match with any of the supporting Address Proof Documents - ${EzUtils.joinArray(
              ", ", this.readableAllowedAddressProofs)}.`
          }

        case AddressResponse.PERMANENT_ADDRESS_TYPE:
          return `Please enter ${addressOwner} Permanent address`;

        case AddressResponse.COMMUNICATION_ADDRESS_TYPE:
          return `Please enter ${addressOwner} Communication address`;

        case AddressResponse.OFFICE_OR_WORK_ADDRESS_TYPE:
          return `Please enter ${addressOwner} Company/Business Address`;

        default:
          return `Please enter ${addressOwner} Address`;
      }
    },

    addressDescription() {
      return "(Address Proof will be required later)";
    },

    isAgent() {
      return PrefManager.doesUserHasRole(RolesConstants.AGENT);
    },

    rupeeSign() {
      return RUPEE;
    },

    readableAllowedAddressProofs() {
      let allowedAddressProofs = [];
      const addressList = EzUtils.isNotEmpty(this.allowedAddressProofsList)
        ? this.allowedAddressProofsList
        : DEFAULT_ADDRESS_LIST;
      addressList && addressList.forEach(addressProof => {
        allowedAddressProofs.push(EzUtils.removeUnderscoreFromString(addressProof, ['ID']));
      });
      return allowedAddressProofs;
    },
    
    kycOsvDetailsConfig() {
      return this.kycStageDataConfig ? this.kycStageDataConfig.kycOSVDetailsDto : {};
    },

    okycDataConfig() {
      return this.kycStageDataConfig ? this.kycStageDataConfig.oKycDetails : {};
    },

    checkBoxConfig() {
      return DefaultResourceConfig.CHECK_BOX;
    },

    toolbarTitle() {
        switch (this.addressType) {
            case AddressResponse.HOME_ADDRESS_TYPE:
              return  "Address";

            case AddressResponse.PERMANENT_ADDRESS_TYPE:
              return  "Permanent Address";

            case AddressResponse.COMMUNICATION_ADDRESS_TYPE:
              return  "Communication Address";

            case AddressResponse.OFFICE_OR_WORK_ADDRESS_TYPE:
              return  "Company/Business Address";

            default:
                return "Address";
        }
    },

    fetchLeadAddress() {
      return this.addressType === AddressResponse.HOME_ADDRESS_TYPE || this.addressType === AddressResponse.PERMANENT_ADDRESS_TYPE;
    },

    getAddressLeadType() {
      switch (this.addressType) {
        case AddressResponse.HOME_ADDRESS_TYPE:
          return  "RESIDENCE";

        case AddressResponse.PERMANENT_ADDRESS_TYPE:
          return  "PERMANENT";
      }
    }
  }
}
</script>

<style scoped src="../../assets/style/base.css"></style>
